import { type PointerEvent, useMemo, useRef } from "react";

interface ZoomRulerProps {
  value: number;
  onChange: (value: number) => void;
}

const TICK_COUNT = 81;
const TICK_SPACING = 14;
const SCROLL_DISTANCE = 2.5;

const getTickClassName = (isMajor: boolean, isMidpoint: boolean) => {
  if (isMajor) return "h-6 opacity-60";
  if (isMidpoint) return "h-4 opacity-40";
  return "h-2.5 opacity-20";
};

export function ZoomRuler({ value, onChange }: Readonly<ZoomRulerProps>) {
  const ticks = useMemo(() => Array.from({ length: TICK_COUNT }, (_, index) => index), []);
  const inputRef = useRef<HTMLInputElement>(null);
  const dragState = useRef<{
    pointerId: number;
    startX: number;
    startValue: number;
    lastValue: number;
  } | null>(null);
  const roundedValue = Math.round(value);

  const startDragging = (event: PointerEvent<HTMLDivElement>) => {
    if (!event.isPrimary || event.button !== 0) return;
    event.preventDefault();
    dragState.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startValue: roundedValue,
      lastValue: roundedValue
    };
    event.currentTarget.setPointerCapture?.(event.pointerId);
    inputRef.current?.focus({ preventScroll: true });
  };

  const continueDragging = (event: PointerEvent<HTMLDivElement>) => {
    const drag = dragState.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    event.preventDefault();

    const delta = (event.clientX - drag.startX) / SCROLL_DISTANCE;
    const nextValue = Math.round(Math.max(0, Math.min(100, drag.startValue + delta)));
    if (nextValue === drag.lastValue) return;

    drag.lastValue = nextValue;
    onChange(nextValue);
  };

  const stopDragging = (event: PointerEvent<HTMLDivElement>) => {
    if (dragState.current?.pointerId !== event.pointerId) return;
    dragState.current = null;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
  };

  return (
    <div
      data-testid="zoom-ruler"
      onPointerDown={startDragging}
      onPointerMove={continueDragging}
      onPointerUp={stopDragging}
      onPointerCancel={stopDragging}
      className="relative mb-5 h-16 touch-none select-none overflow-hidden rounded-full border border-white/5 bg-[#1d1d1f] shadow-[inset_0_1px_2px_rgba(255,255,255,0.04),0_8px_24px_rgba(0,0,0,0.22)] cursor-grab active:cursor-grabbing">
      <input
        ref={inputRef}
        type="range"
        min={0}
        max={100}
        step={1}
        value={value}
        onChange={(event) => onChange(Math.round(Number(event.target.value)))}
        className="peer pointer-events-none absolute inset-0 z-20 h-full w-full cursor-ew-resize opacity-0"
        aria-label="Photo zoom"
        aria-valuetext={`${roundedValue}% zoom`}
      />

      <output className="pointer-events-none absolute left-5 top-1/2 z-10 min-w-8 -translate-y-1/2 text-center text-lg font-medium tabular-nums text-white">
        {roundedValue}
      </output>

      <div className="pointer-events-none absolute inset-y-0 left-16 right-5 overflow-hidden">
        <div
          className="absolute left-1/2 top-1/2 flex -translate-y-1/2 items-center will-change-transform"
          style={{
            width: TICK_COUNT * TICK_SPACING,
            transform: `translateX(calc(-50% - ${value * SCROLL_DISTANCE}px))`
          }}>
          {ticks.map((tick) => {
            const isMajor = tick % 10 === 0;
            const isMidpoint = !isMajor && tick % 5 === 0;
            return (
              <span
                key={tick}
                className={`flex-none rounded-full bg-white ${getTickClassName(
                  isMajor,
                  isMidpoint
                )}`}
                style={{ width: 2, marginRight: TICK_SPACING - 2 }}
              />
            );
          })}
        </div>

        <span className="absolute bottom-2.5 left-1/2 top-2.5 z-10 w-px -translate-x-1/2 rounded-full bg-white shadow-[0_0_7px_rgba(255,255,255,0.5)]" />
        <span className="absolute inset-y-0 left-0 z-10 w-10 bg-gradient-to-r from-[#1d1d1f] to-transparent" />
        <span className="absolute inset-y-0 right-0 z-10 w-10 bg-gradient-to-l from-[#1d1d1f] to-transparent" />
      </div>

      <span className="pointer-events-none absolute inset-0 rounded-full ring-inset peer-focus-visible:ring-2 peer-focus-visible:ring-white" />
    </div>
  );
}
